import { useContext } from 'react';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { Feature } from 'src/WebApiController';
import { useContextSelector } from 'use-context-selector';

import {
  MultiSelectionContext,
  MultiSelectionContextWithSelector,
} from './MultiSelectionContext';
import {
  GroupStateInput,
  ItemSelection,
  ItemSelectionValue,
  MultiSelectionContextType,
  SelectionMode,
} from './MultiSelectionContext.types';

const useMultiSelectionContextValueSelector = <T>(
  selector: (context: MultiSelectionContextType) => T
): T => {
  const hasContextWithSelectorFeature = useUserHasFeature(
    Feature.FEPerf_CatalogDataContextWithSelector
  );
  const contextValue = useContextSelector(
    MultiSelectionContextWithSelector,
    selector
  );
  const context = useContext(MultiSelectionContext);
  return hasContextWithSelectorFeature ? contextValue : selector(context);
};

export const useMultiSelectionContextSelectionModeSelector = (): readonly [
  SelectionMode | undefined,
  (mode?: SelectionMode, groupId?: string) => void,
] => {
  const selectionMode = useMultiSelectionContextValueSelector(
    (context) => context.selectionMode
  );
  const setSelectionMode = useMultiSelectionContextValueSelector(
    (context) => context.setSelectionMode
  );
  return [selectionMode, setSelectionMode];
};

export const useMultiSelectionContextIsAllSelectedSelector = (): boolean => {
  return useMultiSelectionContextValueSelector(
    (context) => context.isAllSelected
  );
};

export const useMultiSelectionContextTotalItemsSelectedSelector =
  (): number => {
    return useMultiSelectionContextValueSelector(
      (context) => context.totalItemsSelected
    );
  };

export const useMultiSelectionContextItemSelectionSelector =
  (): ItemSelection => {
    return useMultiSelectionContextValueSelector(
      (context) => context.itemSelection
    );
  };

export const useMultiSelectionContextGetSelectionSelector = (): ((
  groupId?: string
) => {
  groupIds: string[];
  items: { groupIds: string[]; itemIds: string[] };
  isSingleGroupMultiSelect: boolean;
}) => {
  return useMultiSelectionContextValueSelector(
    (context) => context.getSelection
  );
};

export const useMultiSelectionContextGetGroupToggleStateSelector = (): ((
  groupId: string
) => ItemSelectionValue) => {
  return useMultiSelectionContextValueSelector(
    (context) => context.getGroupToggleState
  );
};

export const useMultiSelectionContextGetItemToggleStateSelector = (): ((
  itemId: string,
  groupId: string
) => boolean) => {
  return useMultiSelectionContextValueSelector(
    (context) => context.getItemToggleState
  );
};

export const useMultiSelectionContextEnableSelectionByDefaultSelector =
  (): boolean => {
    return useMultiSelectionContextValueSelector(
      (context) => context.enableSelectionByDefault
    );
  };

export const useMultiSelectionContextToggleGroupSelector = (): ((
  groupId: string
) => void) => {
  return useMultiSelectionContextValueSelector(
    (context) => context.toggleGroup
  );
};

export const useMultiSelectionContextSetGroupItemsSelector = (): ((
  groupId: string,
  items: GroupStateInput
) => void) => {
  return useMultiSelectionContextValueSelector(
    (context) => context.setGroupItems
  );
};

export const useMultiSelectionContextToggleSelectAllSelector =
  (): (() => void) => {
    return useMultiSelectionContextValueSelector(
      (context) => context.toggleSelectAll
    );
  };

export const useMultiSelectionContextAllGroupsIdsSelector = (): readonly [
  string[],
  (groupsIds: string[]) => void,
] => {
  const allGroupsIds = useMultiSelectionContextValueSelector(
    (context) => context.allGroupsIds
  );
  const setAllGroupsIds = useMultiSelectionContextValueSelector(
    (context) => context.setAllGroupsIds
  );
  return [allGroupsIds, setAllGroupsIds];
};

export const useMultiSelectionContextShouldHideCheckboxesSelector =
  (): boolean => {
    return useMultiSelectionContextValueSelector(
      (context) => context.shouldHideCheckboxes
    );
  };

export const useMultiSelectionContextSetGroupStatesSelector = (): ((
  groups: GroupStateInput
) => void) => {
  return useMultiSelectionContextValueSelector(
    (context) => context.setGroupStates
  );
};

export const useMultiSelectionContextClearShiftKeySelectionSelector =
  (): (() => void) => {
    return useMultiSelectionContextValueSelector(
      (context) => context.clearShiftKeySelection
    );
  };

export const useMultiSelectionContextSetSelectionModeSelector = (): ((
  mode?: SelectionMode,
  groupId?: string
) => void) => {
  return useMultiSelectionContextValueSelector(
    (context) => context.setSelectionMode
  );
};
